import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Evaluation } from '@/types/evaluation.types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { XCircle, AlertTriangle } from 'lucide-react';

interface CloseDialogProps {
  evaluation: Evaluation;
  onClose: (evaluation: Evaluation, reason?: string) => void;
  children: React.ReactNode;
}

const SUGGESTED_REASONS = [
  { value: 'cancelled', label: 'Project cancelled' },
  { value: 'requirements', label: 'Requirements changed' },
  { value: 'vendor', label: 'Vendor withdrew' },
  { value: 'budget', label: 'Budget constraints' },
  { value: 'other', label: 'Other (specify below)' },
];

export function CloseDialog({
  evaluation,
  onClose,
  children,
}: CloseDialogProps) {
  const { t } = useTranslation('dashboard');
  const [open, setOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [customReason, setCustomReason] = useState('');

  const handleClose = () => {
    const finalReason =
      selectedReason === 'other'
        ? customReason.trim()
        : SUGGESTED_REASONS.find((r) => r.value === selectedReason)?.label || '';

    onClose(evaluation, finalReason || undefined);
    setOpen(false);
    setSelectedReason('');
    setCustomReason('');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-gray-600" aria-hidden="true" />
            {t('status.close.title')}
          </DialogTitle>
          <DialogDescription>{t('status.close.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Warning message */}
          <div className="rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 p-4 flex items-start gap-3">
            <AlertTriangle
              className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0"
              aria-hidden="true"
            />
            <div className="space-y-1">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                {t('status.close.warning')}
              </p>
              <p className="text-sm text-amber-800 dark:text-amber-300">
                Participants will no longer be able to edit or submit scorecards.
              </p>
            </div>
          </div>

          {/* Reason selection */}
          <div className="space-y-2">
            <Label htmlFor="close-reason">{t('status.close.reasonLabel')}</Label>
            <Select value={selectedReason} onValueChange={setSelectedReason}>
              <SelectTrigger id="close-reason">
                <SelectValue placeholder="Select a reason (optional)" />
              </SelectTrigger>
              <SelectContent>
                {SUGGESTED_REASONS.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Custom reason textarea (only if "Other" selected) */}
          {selectedReason === 'other' && (
            <div className="space-y-2">
              <Label htmlFor="custom-reason">Custom Reason</Label>
              <Textarea
                id="custom-reason"
                placeholder={t('status.close.reasonPlaceholder')}
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                maxLength={500}
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                {customReason.length}/500 characters
              </p>
            </div>
          )}

          {/* Information */}
          <div className="rounded-md bg-muted p-4">
            <p className="text-sm text-muted-foreground">
              You can reactivate this evaluation later if needed.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleClose} variant="destructive">
            <XCircle className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('status.close.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
